// LF-S2: in-memory project editing session.
//
// A reducer-based model that holds the whole project (tasks, roles, metadata)
// in client memory. Every mutation updates session state only — nothing hits
// the network until `buildSavePayload` is sent to POST /api/projects/{id}/save.
//
// State shape:
//   loadedSnapshot — immutable copy of the last server state
//   current        — working state, mutated by user actions
//   operations     — what changed since load (used to build the save diff)
//   tempIdCounter  — next negative id for newly-created tasks/roles
//
// `operations.tasks.create` / `roles.create` hold the temp ids of new rows;
// the rows themselves live in `current`, so the two never diverge. `update`
// is a Map of realId -> changed fields; `delete` is a Set of real ids.

import { buildRows, recalcDates, toLocalIsoDate } from './planUtils';

// ── snapshot helpers ────────────────────────────────────────────────────
const cloneTask = (t) => ({ ...t, roles: t.roles ? { ...t.roles } : undefined });
const cloneRole = (r) => ({ ...r });

function cloneSnapshot(s) {
  const src = s || {};
  const project = { ...(src.project || {}) };
  return {
    project,
    tasks: (src.tasks || []).map(cloneTask),
    roles: (src.roles || []).map(cloneRole),
    version: src.version ?? project.version ?? 0,
  };
}

function emptyOperations() {
  // SAVECOUNT-S1 — two separate buckets to keep the user-facing dirty
  // count honest:
  //   - tasks.update:   USER updates only (field edits, status, name,
  //                     notes, manual predecessor changes). Each entry
  //                     adds 1 to the visible count.
  //   - tasks.predRemap: Map<id, sortOrder> for auto-generated
  //                     predecessor remaps written by REORDER_TASKS
  //                     (NF-S4). These keep the wire-format correct
  //                     (every downstream pred still names the same
  //                     task) but must NOT show as user changes.
  //   - tasks.reordered:    structural marker — payload still ships a
  //                         reorder envelope when true.
  //   - tasks.userReordered: dirty-count marker — only true for genuine
  //                         user-initiated reorders (drag, indent,
  //                         outdent, move). Side-effect reorders from
  //                         an insert pass { sideEffect: true } so
  //                         this stays false.
  return {
    tasks: {
      create: [],
      update: new Map(),
      delete: new Set(),
      predRemap: new Map(),
      reordered: false,
      userReordered: false,
    },
    roles: { create: [], update: new Map(), delete: new Set() },
    projectChanged: false,
  };
}

function cloneOperations(o) {
  return {
    tasks: {
      create: [...o.tasks.create],
      update: new Map(o.tasks.update),
      delete: new Set(o.tasks.delete),
      predRemap: new Map(o.tasks.predRemap || []),
      reordered: o.tasks.reordered,
      userReordered: !!o.tasks.userReordered,
    },
    roles: {
      create: [...o.roles.create],
      update: new Map(o.roles.update),
      delete: new Set(o.roles.delete),
    },
    projectChanged: o.projectChanged,
  };
}

// Indices/ids of the contiguous block of tasks nested under tasks[startIdx]
// (anything immediately after it with a deeper outline level).
function descendantIds(tasks, startIdx) {
  const base = tasks[startIdx].outlineLevel ?? 0;
  const ids = [];
  for (let i = startIdx + 1; i < tasks.length; i++) {
    if ((tasks[i].outlineLevel ?? 0) > base) ids.push(tasks[i].id);
    else break;
  }
  return ids;
}

export function createInitialSession(snapshot) {
  return {
    loadedSnapshot: cloneSnapshot(snapshot),
    current: cloneSnapshot(snapshot),
    operations: emptyOperations(),
    tempIdCounter: -1,
  };
}

// ── action creators ─────────────────────────────────────────────────────
export const updateTaskField = (taskId, field, value) =>
  ({ type: 'UPDATE_TASK_FIELD', taskId, field, value });
// UX-S2 — snapshot-only sibling of updateTaskField. Used after an
// out-of-band immediate save (read-only WBS Done / Notes) where the
// value is already persisted server-side; the action must NOT add a
// diff entry, so the session stays clean and the navigation guards
// don't fire false "unsaved changes" prompts.
export const setTaskFieldLocal = (taskId, field, value) =>
  ({ type: 'SET_TASK_FIELD_LOCAL', taskId, field, value });
export const addTask = (template = {}) => ({ type: 'ADD_TASK', template });
// WBS-FIX-S2 — snapshot-only sibling of addTask. Used after the
// consultant-side immediate POST to /my-projects/{id}/tasks/wbs: the
// row already exists server-side, so it must NOT enter operations.create
// (which would dirty the session and double-save on the PM side).
export const addTaskLocal = (task) => ({ type: 'ADD_TASK_LOCAL', task });
// WBS-FIX-S2 — snapshot-only delete sibling. Same rationale as
// addTaskLocal: the DELETE has already hit the server.
export const deleteTaskLocal = (taskId) => ({ type: 'DELETE_TASK_LOCAL', taskId });
export const deleteTask = (taskId) => ({ type: 'DELETE_TASK', taskId });
// SAVECOUNT-S1 — opts.sideEffect=true marks a reorder that is the
// consequence of an insert (the post-addTask splice in addChildTask /
// addWbsTaskReadOnly / insertTask). The payload still emits the reorder
// envelope so the server sees the new SortOrder, but the user-facing
// dirtyCount must not double-count the insert. Genuine user reorders
// (drag, indent, outdent, move) pass NO opts.
export const reorderTasks = (orderedIds, opts) =>
  ({ type: 'REORDER_TASKS', orderedIds, sideEffect: !!(opts && opts.sideEffect) });
export const updateRoleField = (roleId, field, value) =>
  ({ type: 'UPDATE_ROLE_FIELD', roleId, field, value });
export const addRole = (template = {}) => ({ type: 'ADD_ROLE', template });
export const deleteRole = (roleId) => ({ type: 'DELETE_ROLE', roleId });
export const updateProjectMetadata = (patch) => ({ type: 'UPDATE_PROJECT_METADATA', patch });
export const discard = () => ({ type: 'DISCARD' });
export const applySaveResult = (idMap, newVersion) =>
  ({ type: 'APPLY_SAVE_RESULT', idMap, newVersion });

// ── reducer ─────────────────────────────────────────────────────────────
export function projectSessionReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_TASK_FIELD': {
      const { taskId, field, value } = action;
      const tasks = state.current.tasks.map((t) =>
        t.id === taskId ? { ...t, [field]: value } : t);
      const ops = cloneOperations(state.operations);
      // Real tasks accumulate a field-level diff; temp tasks need no diff
      // entry — their create payload is read straight from `current`.
      if (taskId >= 0) {
        const existing = ops.tasks.update.get(taskId) || {};
        // SAVECOUNT-S1 — if this task currently sits only in predRemap
        // (auto-generated remap from a renumber), promote it now: the
        // user is doing a real edit on it, so it should count as one
        // user change AND the remapped predecessor must ride along.
        const remappedPred = ops.tasks.predRemap.has(taskId)
          ? { predecessorSortOrder: ops.tasks.predRemap.get(taskId) }
          : null;
        ops.tasks.update.set(taskId, { ...remappedPred, ...existing, [field]: value });
        if (remappedPred) ops.tasks.predRemap.delete(taskId);
      }
      return { ...state, current: { ...state.current, tasks }, operations: ops };
    }

    case 'SET_TASK_FIELD_LOCAL': {
      // UX-S2 — snapshot-only update for a field that was ALREADY persisted
      // out of band (e.g. read-only WBS immediate save). Must NOT touch
      // state.operations, so the session stays clean (no dirty flag, no
      // beforeunload guard, no navigation blocker).
      const { taskId, field, value } = action;
      const tasks = state.current.tasks.map((t) =>
        t.id === taskId ? { ...t, [field]: value } : t);
      return { ...state, current: { ...state.current, tasks } };
    }

    case 'ADD_TASK': {
      const tempId = state.tempIdCounter;
      const task = { ...action.template, id: tempId };
      const ops = cloneOperations(state.operations);
      ops.tasks.create = [...ops.tasks.create, tempId];
      return {
        ...state,
        current: { ...state.current, tasks: [...state.current.tasks, task] },
        operations: ops,
        tempIdCounter: state.tempIdCounter - 1,
      };
    }

    case 'ADD_TASK_LOCAL': {
      // WBS-FIX-S2 — snapshot-only insert. No operations diff; the row
      // was already created server-side by the consultant POST and
      // mustn't ride along on the next PM Save.
      const incoming = action.task;
      if (!incoming || incoming.id == null) return state;
      // De-dup: if the task somehow already exists (e.g. a second click),
      // just refresh its fields rather than appending a duplicate.
      const idx = state.current.tasks.findIndex((t) => t.id === incoming.id);
      const tasks = idx >= 0
        ? state.current.tasks.map((t, i) => i === idx ? { ...t, ...incoming } : t)
        : [...state.current.tasks, incoming];
      return { ...state, current: { ...state.current, tasks } };
    }

    case 'DELETE_TASK_LOCAL': {
      // WBS-FIX-S2 — snapshot-only delete. Mirrors ADD_TASK_LOCAL.
      const { taskId } = action;
      const tasks = state.current.tasks.filter((t) => t.id !== taskId);
      return { ...state, current: { ...state.current, tasks } };
    }

    case 'DELETE_TASK': {
      const { taskId } = action;
      const all = state.current.tasks;
      const startIdx = all.findIndex((t) => t.id === taskId);
      if (startIdx < 0) return state;

      // LF-S5: cascading delete — a summary task takes its descendants with it.
      const toDelete = new Set([taskId, ...descendantIds(all, startIdx)]);
      // LF-S5: predecessor cleanup — remember the sort orders being removed.
      const deletedSortOrders = new Set(
        all.filter((t) => toDelete.has(t.id))
          .map((t) => t.sortOrder)
          .filter((s) => s != null));

      const ops = cloneOperations(state.operations);
      for (const id of toDelete) {
        if (id < 0) {
          // Temp task never reached the server — just drop the create entry.
          ops.tasks.create = ops.tasks.create.filter((c) => c !== id);
        } else {
          ops.tasks.delete.add(id);
          ops.tasks.update.delete(id); // a deleted task needs no field diff
          ops.tasks.predRemap.delete(id); // SAVECOUNT-S1 — same for the remap bucket
        }
      }

      let tasks = all.filter((t) => !toDelete.has(t.id));
      // Any surviving task that depended on a deleted task loses its predecessor.
      // SAVECOUNT-S1 — that pred clear is structural (just like a remap from a
      // REORDER), so route it through predRemap unless the task already has a
      // user update entry. The buildSavePayload merge ships it either way.
      tasks = tasks.map((t) => {
        if (t.predecessorSortOrder != null && deletedSortOrders.has(t.predecessorSortOrder)) {
          if (t.id >= 0) {
            if (ops.tasks.update.has(t.id)) {
              ops.tasks.update.set(t.id, {
                ...ops.tasks.update.get(t.id), predecessorSortOrder: null,
              });
            } else {
              ops.tasks.predRemap.set(t.id, null);
            }
          }
          return { ...t, predecessorSortOrder: null };
        }
        return t;
      });
      return { ...state, current: { ...state.current, tasks }, operations: ops };
    }

    case 'REORDER_TASKS': {
      const { orderedIds } = action;
      const byId = new Map(state.current.tasks.map((t) => [t.id, t]));
      // NF-S4 — snapshot the OLD sortOrder → task.id map so we can rewrite
      // every `predecessorSortOrder` after the reassignment. Without this,
      // any predecessor that used to point to e.g. sortOrder=3 silently
      // ends up pointing at whoever happens to land on sortOrder=3 after
      // the renumber, which is wrong for any insert that shifts positions.
      const idByOldSort = new Map();
      for (const t of state.current.tasks) {
        if (t.sortOrder != null) idByOldSort.set(t.sortOrder, t.id);
      }
      const reordered = orderedIds
        .map((id, idx) => {
          const t = byId.get(id);
          return t ? { ...t, sortOrder: idx + 1 } : null;
        })
        .filter(Boolean);
      // keep any task not named in orderedIds, appended in original order
      for (const t of state.current.tasks) {
        if (!orderedIds.includes(t.id)) reordered.push(t);
      }
      const newSortById = new Map();
      for (const t of reordered) newSortById.set(t.id, t.sortOrder);

      const ops = cloneOperations(state.operations);
      ops.tasks.reordered = true;
      // SAVECOUNT-S1 — only a user-initiated reorder bumps the count.
      if (!action.sideEffect) ops.tasks.userReordered = true;

      // NF-S4 — remap each task's predecessorSortOrder so it still names
      // the SAME upstream task. New tasks (id < 0) carry their new pred
      // in `ops.tasks.create` already.
      //
      // SAVECOUNT-S1 — for real tasks, route the remapped predecessor to
      // `ops.tasks.predRemap` instead of `ops.tasks.update`. That keeps
      // the wire format identical (buildSavePayload merges predRemap
      // into the emitted tasks.update list) but stops these structural
      // entries from inflating dirtyCount. If the task already has a
      // user update entry, merge the remapped predecessor into it (it's
      // already counted as one user change).
      const remapped = reordered.map((t) => {
        if (t.predecessorSortOrder == null) return t;
        const predTaskId = idByOldSort.get(t.predecessorSortOrder);
        if (predTaskId == null) return t; // dangling reference — leave it
        const nextPred = newSortById.get(predTaskId);
        if (nextPred == null || nextPred === t.predecessorSortOrder) return t;
        if (t.id >= 0) {
          if (ops.tasks.update.has(t.id)) {
            // Already a counted user change — merge the remap into it.
            ops.tasks.update.set(t.id, {
              ...ops.tasks.update.get(t.id),
              predecessorSortOrder: nextPred,
            });
          } else {
            ops.tasks.predRemap.set(t.id, nextPred);
          }
        }
        return { ...t, predecessorSortOrder: nextPred };
      });

      return { ...state, current: { ...state.current, tasks: remapped }, operations: ops };
    }

    case 'UPDATE_ROLE_FIELD': {
      const { roleId, field, value } = action;
      const roles = state.current.roles.map((r) =>
        r.id === roleId ? { ...r, [field]: value } : r);
      const ops = cloneOperations(state.operations);
      if (roleId >= 0) {
        ops.roles.update.set(roleId, { ...(ops.roles.update.get(roleId) || {}), [field]: value });
      }
      return { ...state, current: { ...state.current, roles }, operations: ops };
    }

    case 'ADD_ROLE': {
      const tempId = state.tempIdCounter;
      const role = { ...action.template, id: tempId };
      const ops = cloneOperations(state.operations);
      ops.roles.create = [...ops.roles.create, tempId];
      return {
        ...state,
        current: { ...state.current, roles: [...state.current.roles, role] },
        operations: ops,
        tempIdCounter: state.tempIdCounter - 1,
      };
    }

    case 'DELETE_ROLE': {
      const { roleId } = action;
      const roles = state.current.roles.filter((r) => r.id !== roleId);
      const ops = cloneOperations(state.operations);
      if (roleId < 0) {
        ops.roles.create = ops.roles.create.filter((id) => id !== roleId);
      } else {
        ops.roles.delete.add(roleId);
        ops.roles.update.delete(roleId);
      }
      return { ...state, current: { ...state.current, roles }, operations: ops };
    }

    case 'UPDATE_PROJECT_METADATA': {
      const ops = cloneOperations(state.operations);
      ops.projectChanged = true;
      return {
        ...state,
        current: { ...state.current, project: { ...state.current.project, ...action.patch } },
        operations: ops,
      };
    }

    case 'DISCARD': {
      return {
        ...state,
        current: cloneSnapshot(state.loadedSnapshot),
        operations: emptyOperations(),
        tempIdCounter: -1,
      };
    }

    case 'APPLY_SAVE_RESULT': {
      const { idMap, newVersion } = action;
      const taskMap = new Map((idMap?.tasks || []).map((m) => [m.tempId, m.realId]));
      const roleMap = new Map((idMap?.roles || []).map((m) => [m.tempId, m.realId]));

      const remapAllocations = (roles) => {
        if (!roles) return roles;
        const out = {};
        for (const [key, val] of Object.entries(roles)) {
          const numKey = Number(key);
          const realKey = numKey < 0 && roleMap.has(numKey) ? roleMap.get(numKey) : numKey;
          out[realKey] = val;
        }
        return out;
      };

      const tasks = state.current.tasks.map((t) => {
        const next = { ...t, roles: remapAllocations(t.roles) };
        if (t.id < 0 && taskMap.has(t.id)) next.id = taskMap.get(t.id);
        return next;
      });
      const roles = state.current.roles.map((r) => {
        if (r.id < 0 && roleMap.has(r.id)) return { ...r, id: roleMap.get(r.id) };
        return { ...r };
      });

      const version = newVersion ?? state.current.version;
      const savedSnapshot = {
        project: { ...state.current.project, version },
        tasks,
        roles,
        version,
      };
      return {
        ...state,
        current: cloneSnapshot(savedSnapshot),
        loadedSnapshot: cloneSnapshot(savedSnapshot),
        operations: emptyOperations(),
        tempIdCounter: -1,
      };
    }

    default:
      return state;
  }
}

// ── selectors ───────────────────────────────────────────────────────────
// SAVECOUNT-S1 — `isDirty` (Save-button enable) still flips on ANY
// structural change, including a side-effect reorder from an insert
// and any auto-generated predecessor remap. The visible `dirtyCount`
// counts only user-initiated changes.
export function isDirty(state) {
  const o = state.operations;
  return (
    o.tasks.create.length > 0 ||
    o.tasks.update.size > 0 ||
    o.tasks.delete.size > 0 ||
    o.tasks.reordered ||
    (o.tasks.predRemap && o.tasks.predRemap.size > 0) ||
    o.roles.create.length > 0 ||
    o.roles.update.size > 0 ||
    o.roles.delete.size > 0 ||
    o.projectChanged
  );
}

export function dirtyCount(state) {
  const o = state.operations;
  return (
    o.tasks.create.length +
    o.tasks.update.size +              // user updates only
    o.tasks.delete.size +
    (o.tasks.userReordered ? 1 : 0) +  // side-effect reorders don't count
    o.roles.create.length +
    o.roles.update.size +
    o.roles.delete.size +
    (o.projectChanged ? 1 : 0)
  );
}

// Role allocations may be stored either as { roleId: hours } or as the
// detail-grid shape { roleId: { plannedHours } }. Normalise to { roleId: hours }.
function flattenAllocations(src) {
  if (!src) return undefined;
  const out = {};
  for (const [key, val] of Object.entries(src)) {
    out[Number(key)] = (val && typeof val === 'object') ? Number(val.plannedHours) || 0 : Number(val) || 0;
  }
  return out;
}

function extractAllocations(task) {
  return flattenAllocations(task.roleAllocations || task.roles);
}

// PE-S3: a task's persisted dates (plan §2.3). A MANUAL task with a user-input
// date sends that date; an AUTO task always sends the date computed by
// recalcDates — AUTO dates are derived, never user-input, and must reflect any
// upstream shift even if the task object still carries a stale stored date.
// DATE-TZ-FIX-S1 — both branches serialise as 'yyyy-MM-dd' so the diff
// comparison in buildSavePayload (string equality) treats a stored UTC-iso
// loaded date and the freshly-computed local date consistently.
function pickDates(t, computedDateById) {
  const computed = computedDateById.get(t.id) || { start: null, finish: null };
  const computedStart = computed.start ? toLocalIsoDate(computed.start) : null;
  const computedFinish = computed.finish ? toLocalIsoDate(computed.finish) : null;
  const isManual = (t.taskMode || 'AUTO') === 'MANUAL';
  const manualStart = (isManual && t.plannedStartDate) ? toLocalIsoDate(t.plannedStartDate) : null;
  const manualFinish = (isManual && t.plannedEndDate) ? toLocalIsoDate(t.plannedEndDate) : null;
  return {
    plannedStartDate: manualStart ?? computedStart,
    plannedEndDate: manualFinish ?? computedFinish,
  };
}

function taskToCreateDto(t, computedDateById) {
  const { plannedStartDate, plannedEndDate } = pickDates(t, computedDateById);
  // WBS-S3 — description/notes are session-editable and must ride along
  // on Create (consultant adds a WBS, then types Name + Notes in the
  // drawer; one Save persists everything). Omit when null so PM creates
  // that never touched the fields don't ship empty strings.
  const dto = {
    tempId: t.id,
    name: t.name ?? '',
    outlineLevel: t.outlineLevel ?? 0,
    durationDays: t.durationDays ?? 1,
    taskMode: t.taskMode ?? 'AUTO',
    predecessorSortOrder: t.predecessorSortOrder ?? null,
    plannedStartDate,
    plannedEndDate,
    sortOrder: t.sortOrder ?? 0,
    isMilestone: !!t.isMilestone,
    status: t.status ?? 'NotStarted',
    roleAllocations: extractAllocations(t),
  };
  if (t.description != null) dto.description = t.description;
  if (t.notes != null) dto.notes = t.notes;
  return dto;
}

function roleToCreateDto(r) {
  // ProjectRole has only `Abbreviation` (no separate Code) — populate
  // abbreviation; the server's `Abbreviation ?? Code` fallback covers the rest.
  return {
    tempId: r.id,
    name: r.roleName ?? r.name ?? '',
    abbreviation: r.abbreviation ?? '',
    sellRatePerHour: Number(r.sellRatePerHour) || 0,
    costRatePerHour: Number(r.costRatePerHour) || 0,
  };
}

// NF-S4 — `holidays` is an optional `Set<string>` of YYYY-MM-DD ISO
// dates that AUTO date math should skip alongside weekends. Existing
// callers that pass nothing keep the pre-NF-S4 weekend-only behaviour.
//
// WBS-S3-FIX1 — `wbsOnly` (default false) strips the payload down to
// what the consultant `save-wbs` endpoint accepts:
//   - SKIP the PE-S3 downstream date-diff injection entirely (the loop
//     just below). Without this, a WBS insert that renumbers SortOrder
//     forces recomputed plannedStart/End onto every costed AUTO task —
//     and save-wbs rejects every one of them as ConsultantCannotEditCostedTask.
//   - For each `tasks.update` entry, if the underlying task is NOT WBS,
//     reduce its fields to ONLY `predecessorSortOrder`. WBS tasks keep
//     name/status/notes/description as before.
//   - creates / deletes / reorder are unchanged — save-wbs accepts them.
// PM path (wbsOnly=false) behaves exactly as before.
export function buildSavePayload(state, holidays, wbsOnly = false) {
  const { current, loadedSnapshot, operations: o } = state;
  const findTask = (id) => current.tasks.find((t) => t.id === id);
  const findRole = (id) => current.roles.find((r) => r.id === id);

  // PE-S3: compute effective task dates once (the same recalcDates the UI uses)
  // so AUTO task dates are persisted on Save.
  const computedRows = recalcDates(buildRows(current.tasks), holidays);
  const computedDateById = new Map(
    computedRows.map((r) => [r.id, { start: r._start, finish: r._finish }]),
  );

  // PE-S3: clone the update map, then add date fields for any real task whose
  // computed dates differ from the loaded snapshot — covers downstream tasks
  // shifted by an upstream Duration change that were not otherwise edited.
  // WBS-S3-FIX1: SKIP this entirely in wbsOnly mode (see header note).
  // SAVECOUNT-S1 — start the wire-format update map from a MERGE of the
  // user-update entries and the auto-generated predRemap. A remap-only
  // task emits { id, predecessorSortOrder }; a task with both ends up
  // with the user fields PLUS the remapped predecessor. The wire format
  // is identical to pre-SAVECOUNT-S1 even though only user updates feed
  // dirtyCount.
  const taskUpdates = new Map();
  if (o.tasks.predRemap) {
    for (const [id, pred] of o.tasks.predRemap.entries()) {
      taskUpdates.set(id, { predecessorSortOrder: pred });
    }
  }
  for (const [id, fields] of o.tasks.update.entries()) {
    taskUpdates.set(id, { ...(taskUpdates.get(id) || {}), ...fields });
  }
  if (!wbsOnly) {
    const loadedTaskById = new Map((loadedSnapshot?.tasks || []).map((t) => [t.id, t]));
    for (const t of current.tasks) {
      if (t.id < 0) continue; // new tasks carry their dates via taskToCreateDto
      const { plannedStartDate, plannedEndDate } = pickDates(t, computedDateById);
      const loaded = loadedTaskById.get(t.id) || {};
      // DATE-TZ-FIX-S1 — normalise both sides through toLocalIsoDate so a
      // loaded snapshot in any form (yyyy-MM-dd, yyyy-MM-ddTHH:mm:ssZ, Date)
      // compares like-for-like with the freshly-computed local date.
      const loadedStartIso = toLocalIsoDate(loaded.plannedStartDate) || null;
      const loadedEndIso = toLocalIsoDate(loaded.plannedEndDate) || null;
      const dateChanged =
        (plannedStartDate || null) !== loadedStartIso ||
        (plannedEndDate || null) !== loadedEndIso;
      if (dateChanged) {
        const existing = { ...(taskUpdates.get(t.id) || {}) };
        if (!('plannedStartDate' in existing)) existing.plannedStartDate = plannedStartDate;
        if (!('plannedEndDate' in existing)) existing.plannedEndDate = plannedEndDate;
        taskUpdates.set(t.id, existing);
      }
    }
  }

  const payload = {
    version: loadedSnapshot.version ?? 0,
    tasks: {
      create: o.tasks.create.map((id) => taskToCreateDto(findTask(id), computedDateById)).filter(Boolean),
      update: [...taskUpdates.entries()].map(([id, fields]) => {
        // A `roles` field carries the grid-shape allocation map — the save
        // DTO expects a flat { roleId: hours } under `roleAllocations`.
        const { roles, ...rest } = fields;
        // DP-S3: a cleared predecessor is held as null in the session, but the
        // backend's int? cannot tell an explicit null from an omitted field.
        // Send the explicit `0` clear-signal the backend recognises.
        if ('predecessorSortOrder' in rest && rest.predecessorSortOrder === null) {
          rest.predecessorSortOrder = 0;
        }
        // WBS-S3-FIX1 — strip non-WBS updates down to predecessorSortOrder
        // (the only field save-wbs allows on a costed task). Any other field
        // would surface as ConsultantCannotEditCostedTask. WBS tasks keep
        // their full diff.
        // WBS-RETIRE — the wbsOnly payload-trim path is gone. The
        // consultant save-wbs endpoint now 403s any task op (ASGN-S3)
        // and no code path reaches this branch with wbsOnly=true.
        const dto = { id, ...rest };
        if (roles) dto.roleAllocations = flattenAllocations(roles);
        return dto;
      }),
      delete: [...o.tasks.delete],
      reorder: o.tasks.reordered
        ? current.tasks
            .filter((t) => t.id >= 0)
            .map((t) => ({ id: t.id, sortOrder: t.sortOrder ?? 0 }))
        : [],
    },
    roles: {
      create: o.roles.create.map((id) => roleToCreateDto(findRole(id))).filter(Boolean),
      update: [...o.roles.update.entries()].map(([id, fields]) => ({ id, ...fields })),
      delete: [...o.roles.delete],
    },
  };

  if (o.projectChanged) {
    const p = current.project || {};
    payload.project = {
      name: p.name ?? null,
      clientName: p.clientName ?? null,
      startDate: p.startDate ?? null,
      endDate: p.endDate ?? null,
      status: p.status ?? null,
      currencyCode: p.currencyCode ?? null,
      description: p.description ?? null,
    };
  }

  return payload;
}

// LF-S5: very large diffs are split into sequential saves. Each chunk is a
// valid ProjectSaveDto; the caller threads `version` between calls. Rare —
// only triggers when a single payload would exceed ~900 KB.
const SAVE_BYTE_LIMIT = 900000;
const TASKS_PER_CHUNK = 200;

export function buildSaveChunks(state, holidays, wbsOnly = false) {
  const full = buildSavePayload(state, holidays, wbsOnly);
  if (JSON.stringify(full).length <= SAVE_BYTE_LIMIT) return [full];

  const { create, update, delete: del, reorder } = full.tasks;
  const chunks = [];
  let i = 0;
  let firstChunk = true;
  while (firstChunk || i < create.length || i < update.length) {
    chunks.push({
      version: full.version,
      project: firstChunk ? full.project : undefined,
      tasks: {
        create: create.slice(i, i + TASKS_PER_CHUNK),
        update: update.slice(i, i + TASKS_PER_CHUNK),
        delete: firstChunk ? del : [],
        reorder: firstChunk ? reorder : [],
      },
      roles: firstChunk ? full.roles : { create: [], update: [], delete: [] },
    });
    i += TASKS_PER_CHUNK;
    firstChunk = false;
  }
  return chunks;
}

// ── localStorage serialization (Map/Set-aware) ──────────────────────────
// `JSON.stringify` turns Map/Set into `{}`; this replacer/reviver pair keeps
// the `operations` diff intact so a backed-up session can be fully restored.
function replacer(key, value) {
  if (value instanceof Map) return { __type: 'Map', value: [...value.entries()] };
  if (value instanceof Set) return { __type: 'Set', value: [...value] };
  return value;
}

function reviver(key, value) {
  if (value && value.__type === 'Map') return new Map(value.value);
  if (value && value.__type === 'Set') return new Set(value.value);
  return value;
}

export const serializeSession = (state) => JSON.stringify(state, replacer);
export const deserializeSession = (json) => JSON.parse(json, reviver);
